// DetailsInput component for tag creation form step
import { useMemo, useState } from 'react';
import { Formik } from 'formik';

import {
  Title,
  Label,
  Input,
  Select,
  Button,
  HelperText,
} from '@yaradigitallabs/ahua-react';
import { useAppSelector } from '@/app/hooks';
import {
  selectAssetHierarchy,
  selectAssetHierarchyLoading,
} from '@/features/assetHierarchy/store/assetHierarchy.selectors';
import { config } from '@/config';

import { Colors } from '../../../../public/GlobalTokens';
import {
  dataTypeOptions,
  scheduleOptions,
  signalTypeOptions,
} from '../utils/constants';
import { softTagSchema } from '../utils/softTagValidation.schema';
import type { SoftTagState } from '../utils/types';
import { filterSites } from '../utils/helpers';
import { CreateSoftTagNamingGuideline } from './Guidelines/CreateSoftTagNamingGuideline';

// Props for DetailsInput
// handleConfirm: callback for confirming form values
// handleBack: callback for going back to previous step
type DetailsInputProps = {
  handleConfirm: (values: any) => void;
  handleBack: () => void;
  softTagDetails: SoftTagState | null;
  isEditMode: boolean;
};

// DetailsInput renders a Formik form for soft tag details
export const DetailsInput = ({
  handleConfirm,
  handleBack,
  softTagDetails,
  isEditMode,
}: DetailsInputProps) => {
  const [selectedSites, setSelectedSites] = useState({
    productionSite: '',
    area: '',
    productionUnit: '',
  });
  const assetHierarchy = useAppSelector(selectAssetHierarchy);
  const assetHierarchyLoading = useAppSelector(selectAssetHierarchyLoading);
  const { filteredSites, filteredAreas, filteredUnits } = useMemo(() => {
    return filterSites(
      assetHierarchy,
      selectedSites.productionSite,
      selectedSites.area,
      selectedSites.productionUnit
    );
  }, [selectedSites, assetHierarchy]);

  const showAssetHierarchyWarning = !(
    (Array.isArray(assetHierarchy) && assetHierarchy.length > 0) ||
    assetHierarchyLoading
  );

  const state = {
    productionSite: softTagDetails?.productionSite || '',
    area: softTagDetails?.area || '',
    productionUnit: softTagDetails?.productionUnit || '',
    schedule: softTagDetails?.schedule || '',
    schedulingInterval: softTagDetails?.schedulingInterval || '',
    softTagName: softTagDetails?.softTagName || '',
    description: softTagDetails?.description || '',
    signalName: softTagDetails?.signalName || 'value',
    unit: softTagDetails?.unit || '',
    dataType: softTagDetails?.dataType || '',
    signalType: softTagDetails?.signalType || '',
    owner: softTagDetails?.owner || 'SoftTagManager',
  };

  const isSiteSelectDisabled = useMemo(
    () => assetHierarchyLoading || filteredSites.length === 0,
    [assetHierarchyLoading, filteredSites.length]
  );
  const isAreaSelectDisabled = useMemo(
    () => assetHierarchyLoading || filteredAreas.length === 0,
    [assetHierarchyLoading, filteredAreas.length]
  );
  const isUnitSelectDisabled = useMemo(
    () => assetHierarchyLoading || filteredUnits.length === 0,
    [assetHierarchyLoading, filteredUnits.length]
  );

  return (
    <div className="w-full flex flex-col gap-4 mb-4">
      {/* Formik handles form state, validation, and submission */}
      <Formik
        validateOnMount={true}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={softTagSchema()}
        initialValues={{
          ...state,
        }}
        enableReinitialize={true}
        //   innerRef={formikRef}
        onSubmit={values => {
          handleConfirm(values);
        }}
      >
        {/* Formik render props: form handlers, values, errors, etc. */}
        {({
          handleSubmit,
          values,
          setFieldValue,
          touched,
          errors: {
            productionSite,
            area,
            productionUnit,
            schedule,
            schedulingInterval,
            softTagName,
            description,
            signalName,
            unit,
            dataType,
            signalType,
            owner,
          } = {},
        }: any) => (
          <div className="flex gap-1">
            <form
              className="w-full flex flex-col gap-4 mb-4"
              onSubmit={handleSubmit}
            >
              {/* Soft tag name input */}
              <Input
                variant={
                  softTagName && touched.softTagName ? 'error' : 'default'
                }
                value={values.softTagName}
                size="n"
                label="Soft tag name"
                cover={'fill'}
                onChange={event =>
                  setFieldValue('softTagName', event.target.value)
                }
                disabled={isEditMode}
                required
              />
              {softTagName && touched.softTagName && (
                <HelperText colorConcept="destructive" icon="Info">
                  {typeof softTagName === 'string' ? softTagName : 'Error'}
                </HelperText>
              )}
              {/* Description input */}
              <Input
                variant={
                  description && touched.description ? 'error' : 'default'
                }
                value={values.description}
                size="n"
                label="Description (max 120 characters)"
                cover={'fill'}
                onChange={event =>
                  setFieldValue('description', event.target.value)
                }
                disabled={isEditMode}
                required
              />
              {/* Production site, area, and unit selects */}
              <div>
                <div className="flex flex-row justify-between pb-2">
                  <Select
                    value={values.productionSite}
                    ariaLabel="Select element"
                    cover="fill"
                    css={{
                      width: '30%',
                    }}
                    items={filteredSites}
                    label="Production site"
                    loadingText="Loading..."
                    position="popper"
                    selectContentAutoHeight
                    size="n"
                    onChange={value => {
                      setFieldValue('productionSite', value);
                      setSelectedSites({
                        ...selectedSites,
                        productionSite: value,
                      });
                    }}
                    variant={
                      productionSite && touched.productionSite
                        ? 'error'
                        : 'default'
                    }
                    disabled={isEditMode || isSiteSelectDisabled}
                    required
                  />
                  <Select
                    value={values.area}
                    ariaLabel="Select element"
                    cover="fill"
                    css={{
                      width: '30%',
                    }}
                    items={filteredAreas}
                    label="Area"
                    loadingText="Loading..."
                    position="popper"
                    selectContentAutoHeight
                    size="n"
                    variant={area && touched.area ? 'error' : 'default'}
                    onChange={value => {
                      setFieldValue('area', value);
                      setSelectedSites({ ...selectedSites, area: value });
                    }}
                    disabled={isEditMode || isAreaSelectDisabled}
                    required
                  />
                  <Select
                    value={values.productionUnit}
                    ariaLabel="Select element"
                    cover="fill"
                    css={{
                      width: '30%',
                    }}
                    items={filteredUnits}
                    label="Production unit"
                    loadingText="Loading..."
                    position="popper"
                    selectContentAutoHeight
                    size="n"
                    variant={
                      productionUnit && touched.productionUnit
                        ? 'error'
                        : 'default'
                    }
                    onChange={value => {
                      setFieldValue('productionUnit', value);
                      setSelectedSites({
                        ...selectedSites,
                        productionUnit: value,
                      });
                    }}
                    disabled={isEditMode || isUnitSelectDisabled}
                    required
                  />
                </div>
                {showAssetHierarchyWarning && (
                  <HelperText colorConcept="destructive" icon="Info">
                    The site,area and unit dropdowns are unavailable due to some
                    issues in asset hierarchy api. Please try again later.
                  </HelperText>
                )}
              </div>
              {/* Scheduling options */}
              <Title size="s">Scheduling</Title>
              <div className="flex flex-row justify-between gap-3">
                {scheduleOptions.map(item => (
                  <div
                    key={item.text}
                    className={`p-3 w-[32%] border rounded-md flex flex-row justify-between align-items ${
                      schedule && touched.schedule
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    }`}
                  >
                    <Label size="n">{item.text} </Label>
                    <input
                      type="radio"
                      id={item.value}
                      name="schedule"
                      checked={item.value === values.schedule}
                      onChange={() => {
                        if (item.text === 'Never') {
                          setFieldValue('schedulingInterval', undefined);
                        }
                        setFieldValue('schedule', item.value);
                      }}
                      className={`${
                        schedule && touched.schedule ? 'accent-red-500' : ''
                      }`}
                    />
                  </div>
                ))}
              </div>
              {/* Scheduling interval input */}
              <Input
                variant={
                  schedulingInterval && touched.schedulingInterval
                    ? 'error'
                    : 'default'
                }
                value={values.schedulingInterval}
                size="n"
                label="Scheduling interval"
                cover={'fill'}
                onChange={event =>
                  setFieldValue('schedulingInterval', event.target.value)
                }
                type={'number'}
              />
              {/* Time series section */}
              <Title size="s">Time Series</Title>
              <div className="flex flex-row justify-between gap-8">
                <Input
                  variant={
                    signalName && touched.signalName ? 'error' : 'default'
                  }
                  value={values.signalName}
                  size="n"
                  label="Signal name"
                  cover={'fill'}
                  onChange={event =>
                    setFieldValue('signalName', event.target.value)
                  }
                  disabled={isEditMode}
                  required
                />
                <Input
                  variant={unit && touched.unit ? 'error' : 'default'}
                  value={values.unit}
                  size="n"
                  label="Unit"
                  cover={'fill'}
                  onChange={event => setFieldValue('unit', event.target.value)}
                  disabled={isEditMode}
                  required
                />
              </div>
              <div className="flex flex-row justify-between gap-8">
                <Select
                  value={values.dataType}
                  ariaLabel="Select element"
                  cover="fill"
                  css={{
                    width: '50%',
                  }}
                  items={dataTypeOptions}
                  label="Data type"
                  loadingText="Loading..."
                  position="popper"
                  selectContentAutoHeight
                  size="n"
                  variant={dataType && touched.dataType ? 'error' : 'default'}
                  onChange={value => setFieldValue('dataType', value)}
                  disabled={isEditMode}
                  required
                />
                <Select
                  value={values.signalType}
                  ariaLabel="Select element"
                  cover="fill"
                  css={{
                    width: '50%',
                  }}
                  items={signalTypeOptions}
                  label="Signal type"
                  loadingText="Loading..."
                  position="popper"
                  selectContentAutoHeight
                  size="n"
                  variant={
                    signalType && touched.signalType ? 'error' : 'default'
                  }
                  onChange={value => setFieldValue('signalType', value)}
                  disabled={isEditMode}
                  required
                />
              </div>
              <Title size="s">Author</Title>
              <div className="flex flex-row justify-end">
                <Input
                  variant={owner && touched.owner ? 'error' : 'default'}
                  value={values.owner}
                  size="n"
                  label="Author (optional)"
                  cover={'fill'}
                  onChange={event => setFieldValue('owner', event.target.value)}
                />
              </div>

              <HelperText colorConcept="neutral" icon="Info">
                Please adhere to soft tag creation{' '}
                <a
                  href={config.softTagCreationGuidelineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  style={{ color: Colors.ahua.blue.scale50 }}
                >
                  guidelines
                </a>
                .
              </HelperText>
              {/* Navigation buttons */}
              <div className="flex flex-row align-items justify-end gap-3">
                <Button
                  variant="primary"
                  css={{ width: '70px' }}
                  size="s"
                  onClick={() => handleBack()}
                  colorConcept="inverse"
                  type="button"
                >
                  Back
                </Button>
                <Button css={{ width: '200px' }} size="s" type="submit">
                  Confirm and proceed
                </Button>
              </div>
            </form>
            <div className="mt-px">
              <CreateSoftTagNamingGuideline />
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};
